import { CalendarDays, Clock, MessageSquareLock, NotebookPen } from "lucide-react";
import { Reveal } from "@/components/marketing/Reveal";

const Chapter = ({ num, icon: Icon, title, body, className = "", children }) => (
  <div
    className={`group relative rounded-3xl border border-ink/10 bg-paper p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_12px_30px_-10px_rgba(200,109,81,0.18)] ${className}`}
    data-testid={`manifesto-chapter-${num}`}
  >
    <div className="gold-hairline absolute top-0 left-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs tracking-[0.3em] text-gold font-semibold">{num}</span>
      <Icon className="h-6 w-6 text-sage" />
    </div>
    <h3 className="mt-6 font-serif text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h3>
    <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">{body}</p>
    {children}
  </div>
);

const MiniCalendar = () => (
  <div className="mt-8 grid grid-cols-7 gap-1.5 max-w-md">
    {Array.from({ length: 21 }).map((_, i) => (
      <div
        key={i}
        className={`aspect-square rounded-lg border text-[10px] font-mono flex items-center justify-center ${
          [3, 10, 17].includes(i)
            ? "bg-clay/15 border-clay/40 text-clay"
            : [6, 13].includes(i)
              ? "bg-sage/15 border-sage/40 text-sage"
              : "border-ink/10 text-ink-light"
        }`}
      >
        {i + 1}
      </div>
    ))}
  </div>
);

const ImgCard = ({ src, alt, testid }) => (
  <div className="spotlight-frame relative overflow-hidden rounded-3xl border border-ink/10 min-h-64" data-testid={testid}>
    <img src={src} alt={alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
  </div>
);

export default function Manifesto() {
  return (
    <section id="manifesto" className="py-24 sm:py-32" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay font-semibold">The Manifesto</p>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
            Four chapters. One goal: bonding time.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-6 gap-6">
          <Reveal className="md:col-span-4">
            <Chapter
              num="01"
              icon={CalendarDays}
              title="Visit Scheduling & Approvals"
              body="Parents request visits in a tap; social workers approve with full context. A shared, color-coded calendar both sides trust — agreed once, visible forever."
            >
              <MiniCalendar />
            </Chapter>
          </Reveal>
          <Reveal className="md:col-span-2" delay={0.1}>
            <Chapter
              num="02"
              icon={Clock}
              title="Punctuality & Attendance"
              body="Verified check-ins log arrivals and departures to the minute, building a tamper-evident record of reliability."
            />
          </Reveal>
          <Reveal className="md:col-span-2" delay={0.05}>
            <ImgCard
              src="https://images.unsplash.com/photo-1621275397054-e243eed7e6e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb3RoZXIlMjBodWdnaW5nJTIwY2hpbGQlMjBwZWFjZWZ1bCUyMGhvbWUlMjBzdW5zZXR8ZW58MHx8fHwxNzg4MTkwMTg0fDA&ixlib=rb-4.1.0&q=85"
              alt="Mother holding her child in golden evening light"
              testid="manifesto-image-1"
            />
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.15}>
            <Chapter
              num="03"
              icon={NotebookPen}
              title="Engagement & Case Notes"
              body="Structured notes after every visit — engagement, mood, milestones — written once, assessed easily, and exportable for the case file."
            />
          </Reveal>
          <Reveal className="md:col-span-4">
            <Chapter
              num="04"
              icon={MessageSquareLock}
              title="Secure Communication Vault"
              body="Parents and caseworkers talk in one encrypted, unalterable thread. Say it once — it's documented, encrypted, and done."
            />
          </Reveal>
          <Reveal className="md:col-span-2" delay={0.1}>
            <ImgCard
              src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?q=85&w=1200&auto=format&fit=crop"
              alt="Social worker in a warm conversation with a parent"
              testid="manifesto-image-2"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
